import { CURRENT_SAVE_VERSION } from './schema';
import { createStarterGraph } from '../graph/factory';
import { DEFAULT_RESOURCES } from '../state';
import { MECH_CORE_DEF_ID, MECH_CORE_INSTANCE_ID } from '../data/mechCore';
import { FRAMES } from '../data/frames';
import { OVERLORDS } from '../data/overlords';

/**
 * Ordered, pure migrations. A save from ANY past version must load
 * (TECHNICAL_APPROACH §10). migrate index i maps version (i+1) -> (i+2). One
 * function per bump, each with a migration test.
 */
type Migration = (data: Record<string, unknown>) => Record<string, unknown>;

/** v1 -> v2: introduce the `meta` block (prestige + lifetime stats). */
const migrate_v1_to_v2: Migration = (data) => {
  const wallet = (data.wallet as { scrap?: string } | undefined) ?? {};
  return {
    ...data,
    version: 2,
    meta: { cores: '0', ascensions: 0, lifetimeScrap: typeof wallet.scrap === 'string' ? wallet.scrap : '0' },
  };
};

/**
 * v2 -> v3: the parts/loot/weight rebuild. The old Components-levels-fixed-nodes
 * Mind has no parts equivalent, so we reset the Mind to a fresh starter loadout
 * and seed the inventory, while preserving wallet, frame, wave progress, stats,
 * and meta (no progression lost). Replaces combat with the multi-enemy shape.
 */
const migrate_v2_to_v3: Migration = (data) => {
  const starter = createStarterGraph();
  const oldCombat = (data.combat as Record<string, unknown> | undefined) ?? {};
  const oldMeta = (data.meta as Record<string, unknown> | undefined) ?? {};
  const num = (v: unknown, d: number): number => (typeof v === 'number' ? v : d);
  const str = (v: unknown, d: string): string => (typeof v === 'string' ? v : d);

  return {
    ...data,
    version: 3,
    inventory: {
      parts: starter.parts.map((p) => ({ instanceId: p.instanceId, defId: p.defId, rarity: p.rarity, level: p.level, affixes: [] })),
      nextId: starter.nextPartId,
      cap: DEFAULT_RESOURCES.storage,
    },
    resources: { ...DEFAULT_RESOURCES },
    mind: {
      nodes: starter.graph.nodes.map((n) => ({ ...n })),
      edges: starter.graph.edges.map((e) => ({ ...e })),
      nextId: starter.graph.nextId,
    },
    combat: {
      wave: num(oldCombat.wave, 1),
      highestWave: num(oldCombat.highestWave, 1),
      checkpoint: num(oldCombat.checkpoint, 1),
      enemies: [],
      nextEnemyId: 1,
      attackTimers: {},
      killsThisRun: 0,
      overdriveUntil: 0,
      overdriveCdUntil: 0,
      currentDps: '0',
    },
    meta: {
      cores: str(oldMeta.cores, '0'),
      ascensions: num(oldMeta.ascensions, 0),
      lifetimeScrap: str(oldMeta.lifetimeScrap, '0'),
      unlocks: { mind: true, salvage: true },
    },
  };
};

/** v3 -> v4: add the Core upgrade tree (meta.coreUpgrades). */
const migrate_v3_to_v4: Migration = (data) => {
  const meta = (data.meta as Record<string, unknown> | undefined) ?? {};
  return { ...data, version: 4, meta: { ...meta, coreUpgrades: (meta.coreUpgrades as Record<string, number>) ?? {} } };
};

/** v4 -> v5: enemies gained damage-type resistances. Clear the live horde so it
 *  respawns with resist data on the next tick (no per-enemy backfill needed). */
const migrate_v4_to_v5: Migration = (data) => {
  const combat = (data.combat as Record<string, unknown> | undefined) ?? {};
  return { ...data, version: 5, combat: { ...combat, enemies: [] } };
};

/**
 * v5 -> v6: the intrinsic Mech Core. Power now originates from a singular,
 * upgradeable Core whose output taps grow on Ascension (+ a per-tap splitting
 * penalty). Non-destructively seat a Core in any save that lacks one: add the
 * instance to inventory and an (unwired) node to the graph. Existing wiring and
 * any old auxiliary sources are preserved — the player rewires at leisure.
 */
const migrate_v5_to_v6: Migration = (data) => {
  const mind = (data.mind as { nodes?: unknown[]; edges?: unknown[]; nextId?: number } | undefined) ?? {};
  const inv = (data.inventory as { parts?: unknown[]; nextId?: number; cap?: number } | undefined) ?? {};
  const nodes = Array.isArray(mind.nodes) ? [...mind.nodes] : [];
  const parts = Array.isArray(inv.parts) ? [...inv.parts] : [];
  const hasCore = nodes.some((n) => (n as { instanceId?: unknown }).instanceId === MECH_CORE_INSTANCE_ID);

  let nextId = typeof mind.nextId === 'number' ? mind.nextId : nodes.length + 1;
  if (!hasCore) {
    parts.push({ instanceId: MECH_CORE_INSTANCE_ID, defId: MECH_CORE_DEF_ID, rarity: 'rare', level: 1, affixes: [] });
    nodes.push({ id: `n${nextId}`, defId: MECH_CORE_DEF_ID, instanceId: MECH_CORE_INSTANCE_ID, level: 1, x: 40, y: 30 });
    nextId += 1;
  }

  return {
    ...data,
    version: 6,
    mind: { nodes, edges: Array.isArray(mind.edges) ? mind.edges : [], nextId },
    inventory: { parts, nextId: typeof inv.nextId === 'number' ? inv.nextId : parts.length + 1, cap: typeof inv.cap === 'number' ? inv.cap : DEFAULT_RESOURCES.storage },
  };
};

/**
 * v6 -> v7: the Salvage Drone Bay (first Tech Find). Add the drones block. A save
 * that has already ascended brings the bay online immediately (with starting
 * drones); otherwise it stays locked until the next Ascension.
 */
const migrate_v6_to_v7: Migration = (data) => {
  const meta = (data.meta as { ascensions?: unknown } | undefined) ?? {};
  const ascended = typeof meta.ascensions === 'number' && meta.ascensions > 0;
  return {
    ...data,
    version: 7,
    drones: { unlocked: ascended, bots: ascended ? 3 : 0, broken: 0, clearance: 1, sortie: null },
  };
};

/**
 * v7 -> v8: AI Overlords + Pets + arena pauses. Ascension is now gated on
 * beating the tier's overlord, so any save that already reached a tier gets
 * credit for the overlords below it (no progression is ever taken back). Pets
 * unlock with DJ Leggs (tier 4+ saves have implicitly beaten it).
 */
const migrate_v7_to_v8: Migration = (data) => {
  const combat = (data.combat as Record<string, unknown> | undefined) ?? {};
  const frame = (data.frame as { defId?: unknown } | undefined) ?? {};
  const tier = FRAMES.find((f) => f.id === frame.defId)?.tier ?? 1;
  const defeated = OVERLORDS.filter((o) => o.unlocksTier <= tier).map((o) => o.id);
  return {
    ...data,
    version: 8,
    combat: { ...combat, pauseKind: null, pauseUntilMs: 0, overlordId: null },
    overlords: { defeated },
    pets: { unlocked: tier >= 4, collection: {}, equipped: [] },
  };
};

/** v8 -> v9: Enrage (the stalemate breaker). Encounters track how long they
 *  have dragged; enemy damage ramps with it. Existing fights start calm. */
const migrate_v8_to_v9: Migration = (data) => {
  const combat = (data.combat as Record<string, unknown> | undefined) ?? {};
  return { ...data, version: 9, combat: { ...combat, enrageMs: 0 } };
};

/** v9 -> v10: the Foundry (Sir Loin's part-fusion minigame). Saves that already
 *  beat Sir Loin get it online immediately (same credit rule as v7->v8). */
const migrate_v9_to_v10: Migration = (data) => {
  const overlords = (data.overlords as { defeated?: unknown } | undefined) ?? {};
  const defeated = Array.isArray(overlords.defeated) ? (overlords.defeated as string[]) : [];
  return { ...data, version: 10, foundry: { unlocked: defeated.includes('ovl.sir_loin'), job: null, fused: 0 } };
};

/** v10 -> v11: the Training Yard (Sparky Boi's pet-trial minigame). Same
 *  already-beaten credit rule as the Foundry. */
const migrate_v10_to_v11: Migration = (data) => {
  const overlords = (data.overlords as { defeated?: unknown } | undefined) ?? {};
  const defeated = Array.isArray(overlords.defeated) ? (overlords.defeated as string[]) : [];
  return { ...data, version: 11, yard: { unlocked: defeated.includes('ovl.sparky_boi'), level: 1, trials: [], trained: 0 } };
};

/** v11 -> v12: the Smelter (Sir Cumference's parts-to-Alloy minigame) + the
 *  Alloy wallet. Same already-beaten credit rule as the other minigames. */
const migrate_v11_to_v12: Migration = (data) => {
  const overlords = (data.overlords as { defeated?: unknown } | undefined) ?? {};
  const defeated = Array.isArray(overlords.defeated) ? (overlords.defeated as string[]) : [];
  const wallet = (data.wallet as Record<string, unknown> | undefined) ?? {};
  return {
    ...data,
    version: 12,
    wallet: { ...wallet, alloy: '0' },
    smelter: { unlocked: defeated.includes('ovl.sir_cumference'), queue: [], nextDoneMs: 0, upgrades: {}, smelted: 0 },
  };
};

/** v12 -> v13: Supply Lines (Sir Pent's rail minigame). Same credit rule. */
const migrate_v12_to_v13: Migration = (data) => {
  const overlords = (data.overlords as { defeated?: unknown } | undefined) ?? {};
  const defeated = Array.isArray(overlords.defeated) ? (overlords.defeated as string[]) : [];
  return { ...data, version: 13, rails: { unlocked: defeated.includes('ovl.sir_pent'), routes: [], lastDerailMs: 0, built: 0 } };
};

/** v13 -> v14: Hostile Takeover (MechCorp HQ's dividends desk). Same credit rule. */
const migrate_v13_to_v14: Migration = (data) => {
  const overlords = (data.overlords as { defeated?: unknown } | undefined) ?? {};
  const defeated = Array.isArray(overlords.defeated) ? (overlords.defeated as string[]) : [];
  return {
    ...data,
    version: 14,
    takeover: { unlocked: defeated.includes('ovl.mechcorp_hq'), stakes: {}, nextPayoutMs: 0, lastShiftMs: 0, lastPayout: '0', payouts: 0 },
  };
};

/** v14 -> v15: the Compute Grid (MechCorp Data Center's CPU/RAM/Storage layer).
 *  Credit rule as usual, and RAM pads above any wiring the save already has
 *  so the new wire limit never bites retroactively. */
const migrate_v14_to_v15: Migration = (data) => {
  const overlords = (data.overlords as { defeated?: unknown } | undefined) ?? {};
  const defeated = Array.isArray(overlords.defeated) ? (overlords.defeated as string[]) : [];
  const unlocked = defeated.includes('ovl.mechcorp_dc');
  const resources = { ...((data.resources as Record<string, number> | undefined) ?? {}) };
  const mind = (data.mind as { edges?: unknown[] } | undefined) ?? {};
  const edges = Array.isArray(mind.edges) ? mind.edges.length : 0;
  if (typeof resources.ram === 'number' && resources.ram < edges + 4) resources.ram = edges + 4;
  return {
    ...data,
    version: 15,
    resources,
    grid: { unlocked, run: null, compute: '0', upgrades: {}, intrusions: 0 },
  };
};

/** v15 -> v16: Districts (MechCorp City's population minigame). Credit rule;
 *  population baselines at the save's current frontier. */
const migrate_v15_to_v16: Migration = (data) => {
  const overlords = (data.overlords as { defeated?: unknown } | undefined) ?? {};
  const defeated = Array.isArray(overlords.defeated) ? (overlords.defeated as string[]) : [];
  const combat = (data.combat as { highestWave?: unknown } | undefined) ?? {};
  const frontier = typeof combat.highestWave === 'number' ? combat.highestWave : 1;
  return {
    ...data,
    version: 16,
    districts: {
      unlocked: defeated.includes('ovl.mechcorp_city'),
      baselineWave: frontier,
      assigned: { housing: 0, factory: 0, lab: 0, arcade: 0 },
      popBonus: 0,
    },
  };
};

/** v16 -> v17: Star Harvest (Sol's Dyson swarm megaproject). Credit rule. */
const migrate_v16_to_v17: Migration = (data) => {
  const overlords = (data.overlords as { defeated?: unknown } | undefined) ?? {};
  const defeated = Array.isArray(overlords.defeated) ? (overlords.defeated as string[]) : [];
  return { ...data, version: 17, harvest: { unlocked: defeated.includes('ovl.sol'), satellites: 0, launch: null } };
};

/** v17 -> v18: per-overlord defeat counters (overlord minis). Credit rule:
 *  anything already on the defeated list has been killed at least once. */
const migrate_v17_to_v18: Migration = (data) => {
  const overlords = (data.overlords as { defeated?: unknown } | undefined) ?? {};
  const defeated = Array.isArray(overlords.defeated) ? (overlords.defeated as string[]) : [];
  const defeats: Record<string, number> = {};
  for (const id of defeated) defeats[id] = 1;
  return { ...data, version: 18, overlords: { ...overlords, defeats } };
};

/** v18 -> v19: the stacking-penalty rework (P57). The RAM wire cap is gone;
 *  Compute spent on Memory Lattice ranks is refunded in full (Lattice
 *  Conductance takes its slot in the Grid catalog). resources.ram stays in
 *  the save shape, inert. */
const migrate_v18_to_v19: Migration = (data) => {
  const grid = (data.grid as { upgrades?: Record<string, number>; compute?: unknown } | undefined) ?? {};
  const upgrades = { ...(grid.upgrades ?? {}) };
  const ramRanks = typeof upgrades['ram'] === 'number' ? upgrades['ram'] : 0;
  delete upgrades['ram'];
  // refund what those ranks cost: 18 × 1.5^i per rank i (numbers stay small)
  let refund = 0;
  for (let i = 0; i < ramRanks; i++) refund += 18 * Math.pow(1.5, i);
  const oldCompute = typeof grid.compute === 'string' ? parseFloat(grid.compute) : 0;
  const compute = String((Number.isFinite(oldCompute) ? oldCompute : 0) + refund);
  return { ...data, version: 19, grid: { ...grid, upgrades, compute } };
};

/** v19 -> v20: pets/yard rework. Mini levels move into pets.collection
 *  (seeded from per-overlord defeat counts: one level per 5 kills, drop-cap
 *  10); training can now push any pet past the drop cap toward 25. */
const migrate_v19_to_v20: Migration = (data) => {
  const pets = (data.pets as { collection?: Record<string, number> } | undefined) ?? {};
  const overlords = (data.overlords as { defeats?: Record<string, number> } | undefined) ?? {};
  const collection = { ...(pets.collection ?? {}) };
  for (const [id, kills] of Object.entries(overlords.defeats ?? {})) {
    if (!id.startsWith('ovl.') || typeof kills !== 'number') continue;
    const lvl = Math.min(10, Math.floor(kills / 5));
    if (lvl > 0 && lvl > (collection[id] ?? 0)) collection[id] = lvl;
  }
  return { ...data, version: 20, pets: { ...pets, collection } };
};

/** v20 -> v21: Manual Strike gained a 1s cooldown (combat.manualStrikeCdUntil). */
const migrate_v20_to_v21: Migration = (data) => {
  const combat = (data.combat as Record<string, unknown> | undefined) ?? {};
  return { ...data, version: 21, combat: { ...combat, manualStrikeCdUntil: 0 } };
};

/** v21 -> v22: parts can be protected (inventory.protectedIds), starting empty. */
const migrate_v21_to_v22: Migration = (data) => {
  const inv = (data.inventory as Record<string, unknown> | undefined) ?? {};
  return { ...data, version: 22, inventory: { ...inv, protectedIds: [] } };
};

/** v22 -> v23: the auto-advance/hold toggle (combat.autoAdvance) — on by default,
 *  preserving every existing save's behaviour (waves keep advancing). */
const migrate_v22_to_v23: Migration = (data) => {
  const combat = (data.combat as Record<string, unknown> | undefined) ?? {};
  return { ...data, version: 23, combat: { ...combat, autoAdvance: true } };
};

/** v23 -> v24: Reboot, the endless max-chassis Core loop (meta.reboots). Credit
 *  rule: existing saves have done zero reboots, so default 0. */
const migrate_v23_to_v24: Migration = (data) => {
  const meta = (data.meta as Record<string, unknown> | undefined) ?? {};
  return { ...data, version: 24, meta: { ...meta, reboots: 0 } };
};

// Index i migrates version (i+1) -> (i+2).
const MIGRATIONS: Migration[] = [migrate_v1_to_v2, migrate_v2_to_v3, migrate_v3_to_v4, migrate_v4_to_v5, migrate_v5_to_v6, migrate_v6_to_v7, migrate_v7_to_v8, migrate_v8_to_v9, migrate_v9_to_v10, migrate_v10_to_v11, migrate_v11_to_v12, migrate_v12_to_v13, migrate_v13_to_v14, migrate_v14_to_v15, migrate_v15_to_v16, migrate_v16_to_v17, migrate_v17_to_v18, migrate_v18_to_v19, migrate_v19_to_v20, migrate_v20_to_v21, migrate_v21_to_v22, migrate_v22_to_v23, migrate_v23_to_v24];

export interface MigrationResult {
  data: Record<string, unknown>;
  from: number;
  to: number;
  applied: number;
}

export function migrate(data: Record<string, unknown>, fromVersion: number): MigrationResult {
  if (fromVersion > CURRENT_SAVE_VERSION) {
    throw new Error(`Save is from a newer version (v${fromVersion}) than this build supports (v${CURRENT_SAVE_VERSION}).`);
  }
  let current = data;
  let v = fromVersion;
  let applied = 0;
  while (v < CURRENT_SAVE_VERSION) {
    const migration = MIGRATIONS[v - 1];
    if (!migration) throw new Error(`Missing migration from v${v} to v${v + 1}.`);
    current = migration(current);
    v++;
    applied++;
  }
  return { data: current, from: fromVersion, to: v, applied };
}
